import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

export interface SimpleSelectOption {
  value: string | number;
  label: string;
}

interface SimpleSelectProps {
  options: SimpleSelectOption[];
  value: string | number | null;
  onChange: (value: any) => void;
  placeholder?: string;
  disabled?: boolean;
  minWidth?: string | number;
}

/**
 * Lightweight single-choice dropdown matching the v2 design system.
 * Use for survey pickers, sort selectors, or any simple choice.
 *
 * For multi-select / filter panels, use SelectDropdown's MultiSelectDropdown
 * (see PosFilterModal.tsx) instead.
 */
export const SimpleSelect: React.FC<SimpleSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '—',
  disabled = false,
  minWidth = '100px'
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const activeLabel =
    options.find((o) => o.value === value)?.label || placeholder;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <Wrap ref={ref} style={{ minWidth }}>
      <Trigger
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        $disabled={disabled}
        $open={open}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Label>{activeLabel}</Label>
        <Chevron $open={open}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Chevron>
      </Trigger>
      {open && options.length > 0 && (
        <Dropdown role="listbox">
          {options.map((o) => (
            <Option
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              $active={o.value === value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.value === value && <Check>✓</Check>}
              {o.label}
            </Option>
          ))}
        </Dropdown>
      )}
    </Wrap>
  );
};

// --- Styled ---

const Wrap = styled.div`
  position: relative;
  display: inline-block;
`;

const Trigger = styled.button<{ $disabled: boolean; $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  border: 1px solid
    ${({ $open }) => ($open ? 'var(--terracotta)' : 'var(--line)')};
  border-radius: var(--radius-sm);
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink);
  background: var(--white);
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  transition: border-color 0.15s;
  &:hover {
    border-color: ${({ $disabled }) =>
      $disabled ? 'var(--line)' : 'var(--ink-faint)'};
  }
  &:focus {
    outline: none;
    border-color: var(--terracotta);
  }
`;

const Label = styled.span`
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: flex;
  color: var(--ink-faint);
  transition: transform 0.15s;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 100%;
  max-height: 240px;
  overflow-y: auto;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  z-index: 100;
`;

const Option = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: ${({ $active }) =>
    $active ? 'var(--terracotta)' : 'var(--white)'};
  color: ${({ $active }) => ($active ? '#fff' : 'var(--ink)')};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  &:hover {
    background: ${({ $active }) =>
      $active ? 'var(--terracotta)' : 'var(--paper)'};
  }
`;

const Check = styled.span`
  font-size: 11px;
  line-height: 1;
`;
